import { useEffect, useMemo, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { toast } from "sonner";
import { api } from "@/lib/api";

const matches = (s, ...fields) => fields.some(f => (f || "").toLowerCase().includes(s));

export function usePracticeLibrary() {
  const [plays, setPlays] = useState([]);
  const [drills, setDrills] = useState([]);
  const [playSearch, setPlaySearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [pRes, dRes] = await Promise.all([api.get("/plays"), api.get("/drills")]);
        setPlays(pRes.data);
        setDrills(dRes.data);
      } catch (e) {
        console.error("Failed to load practice library", e);
        toast.error("Failed to load plays and drills. Please try again.");
      }
    })();
  }, []);

  const playsBySide = useMemo(() => {
    const s = playSearch.toLowerCase();
    const groups = { offense: [], defense: [], special_teams: [] };
    plays.filter(pl => !s || matches(s, pl.name, pl.formation))
      .forEach(pl => { (groups[pl.side] = groups[pl.side] || []).push(pl); });
    return groups;
  }, [plays, playSearch]);

  const drillsByCat = useMemo(() => {
    const s = playSearch.toLowerCase();
    const groups = {};
    drills.filter(dr => !s || matches(s, dr.name, dr.category, dr.purpose))
      .forEach(dr => { const c = dr.category || "general"; (groups[c] = groups[c] || []).push(dr); });
    return groups;
  }, [drills, playSearch]);

  return { plays, drills, playSearch, setPlaySearch, playsBySide, drillsByCat };
}

const resolvePeriodId = (over) => {
  if (over.data.current?.type === "period") return over.data.current.periodId;
  const oid = String(over.id);
  if (oid.startsWith("period-drop:")) return oid.slice(12);
  if (oid.startsWith("period:")) return oid.slice(7);
  return null;
};

const linkTo = (periods, periodId, field, id) => periods.map(pd =>
  pd.id !== periodId ? pd : { ...pd, [field]: Array.from(new Set([...(pd[field] || []), id])) });

function reorder(periods, aid, oid) {
  if (!aid.startsWith("period:") || !(oid.startsWith("period:") || oid.startsWith("period-drop:"))) return null;
  const activeId = aid.slice(7);
  const overId = oid.startsWith("period-drop:") ? oid.slice(12) : oid.slice(7);
  if (activeId === overId) return null;
  const oldIdx = periods.findIndex(x => x.id === activeId);
  const newIdx = periods.findIndex(x => x.id === overId);
  if (oldIdx < 0 || newIdx < 0) return null;
  return arrayMove(periods, oldIdx, newIdx);
}

export function usePracticeDnd(periods, update) {
  const [activeDrag, setActiveDrag] = useState(null);

  const onDragStart = ({ active }) => {
    const t = active.data.current?.type;
    if (t === "play") setActiveDrag({ kind: "play", play: active.data.current.play });
    else if (t === "drill") setActiveDrag({ kind: "drill", drill: active.data.current.drill });
    else if (String(active.id).startsWith("period:")) {
      const id = String(active.id).slice(7);
      setActiveDrag({ kind: "period", period: periods.find(x => x.id === id) });
    }
  };

  const onDragEnd = ({ active, over }) => {
    setActiveDrag(null);
    if (!over) return;
    const type = active.data.current?.type;
    if (type === "play" || type === "drill") {
      const periodId = resolvePeriodId(over);
      if (!periodId) return;
      const item = active.data.current[type];
      update({ periods: linkTo(periods, periodId, `${type}_ids`, item.id) });
      toast.success(`Linked ${type}: ${item.name}`);
      return;
    }
    const next = reorder(periods, String(active.id), String(over.id));
    if (next) update({ periods: next });
  };

  return { activeDrag, onDragStart, onDragEnd };
}
